import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { srcSetFor } from "@/lib/responsive";
import { Shield, Award, Hammer, Home, Wrench, Paintbrush, CheckCircle, ArrowRight, Phone, Layers, Square } from "lucide-react";
import GoogleReviews from "@/components/GoogleReviews";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionReveal from "@/components/SectionReveal";
import WaveDivider from "@/components/WaveDivider";
import CTAStrip from "@/components/CTAStrip";
import StickyHomeNav from "@/components/StickyHomeNav";
import heroImg from "@/assets/hero-flooring.jpg";
import heroVideo from "@/assets/hero-flooring-video.mp4";
import logo from "@/assets/logo.png";
import hero640 from "@/assets/responsive/hero-640.webp";
import hero1024 from "@/assets/responsive/hero-1024.webp";
import hero1600 from "@/assets/responsive/hero-1600.webp";
import logo200 from "@/assets/responsive/logo-200.webp";
import logo400 from "@/assets/responsive/logo-400.webp";
import logo600 from "@/assets/responsive/logo-600.webp";
import { useEffect, useState } from "react";

// Desktop only, after page load, and not when the visitor asks to save data.
function useDeferredHeroVideo() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData || !window.matchMedia("(min-width: 768px)").matches) return;
    const start = () => setTimeout(() => setShow(true), 1500);
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
  }, []);
  return show;
}

const heroNavLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Our Work", path: "/our-work" },
  { label: "About", path: "/about" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

const trustBadges = [
  { icon: Shield, label: "100% Satisfaction Guarantee" },
  { icon: Award, label: "BBB Accredited" },
  { icon: Layers, label: "35 Years Experience" },
];

const services = [
  { icon: Home, title: "Hardwood Flooring", desc: "Our specialty for over 35 years. We install beautiful, durable hardwood floors that transform any room — from classic oak to modern finishes. Materials and labor included." },
  { icon: Square, title: "Ceramic Flooring", desc: "Professional ceramic tile installation for kitchens, bathrooms, entryways, and more. Durable, easy to maintain, and available in endless design options to match your style." },
  { icon: Layers, title: "Laminate Flooring", desc: "Affordable laminate flooring that mimics the look of natural wood or stone. A popular choice for homeowners who want stunning results without the premium price tag." },
  { icon: Hammer, title: "Hard Tile", desc: "Expert hard tile installation with precision craftsmanship. Ideal for high-traffic areas that need a durable, long-lasting surface with a clean, professional finish." },
  { icon: Paintbrush, title: "Marble Flooring", desc: "Luxury marble floors for an elegant, timeless finish. We handle the sourcing, cutting, and installation to bring a touch of sophistication to your space." },
  { icon: Wrench, title: "Floor Repairs", desc: "Water damage repair and floor restoration services. We'll fix your flooring even if the damage wasn't caused by our work — that's our commitment to quality." },
];

const steps = [
  {
    title: "Tell Us About Your Project",
    desc: "Give us a call at (773) 934-2004 or fill out the contact form with details about your space and the type of flooring you're interested in. Whether it's a single room or an entire home, we're here to listen.",
  },
  {
    title: "On-Site Visit & Assessment",
    desc: "We'll schedule a time to visit your property and take a close look at the space. This includes checking the subfloor condition, taking precise measurements, and discussing what's possible for your layout.",
  },
  {
    title: "Material Walkthrough & Plan",
    desc: "Before any work begins, we walk you through the materials, explain the plan step by step, and answer every question. You'll know exactly what to expect — no surprises, no hidden costs.",
  },
  {
    title: "Complete Installation",
    desc: "We buy all the materials ourselves and handle the entire installation from start to finish. You don't need to coordinate with suppliers or worry about logistics — that's all on us.",
  },
  {
    title: "Final Walkthrough & Guarantee",
    desc: "Once installation is complete, we do a final walkthrough with you to make sure every detail meets your expectations. Every project is backed by our 100% satisfaction guarantee on all labor.",
  },
];

const whyUs = [
  {
    title: "35 Years of Hands-On Experience",
    desc: "JTB Floors has been installing flooring since day one. With over three decades of hands-on work in hardwood, ceramic, laminate, tile, and marble, we bring deep expertise to every project — big or small.",
  },
  {
    title: "All-Inclusive Service",
    desc: "We don't just install — we handle the entire process. That means we buy all the materials, manage the logistics, and deliver a finished floor. You get one point of contact and zero hassle.",
  },
  {
    title: "100% Satisfaction Guarantee",
    desc: "We stand behind every job with a full satisfaction guarantee on all labor. If something isn't right, we'll make it right — no questions asked. That's the promise we've kept for 35 years.",
  },
  {
    title: "Water Damage? We'll Still Fix It",
    desc: "Even if the water damage wasn't caused by our work, we'll still repair the flooring. That's how much we believe in taking care of our customers and standing behind our craft.",
  },
  {
    title: "Residential & Commercial",
    desc: "From family homes to business properties, we bring the same attention to detail and quality craftsmanship. No project is too large or too small for JTB Floors.",
  },
];

const Index = () => {
  const showVideo = useDeferredHeroVideo();
  return (
    <Layout>
      <StickyHomeNav />
      <SEOHead
        title="Flooring Contractor in Barrington, IL | JTB Floors"
        description="Professional hardwood, ceramic, laminate, tile & marble flooring in Barrington, IL. 35 years experience. 100% satisfaction guarantee. Request a free estimate."
      />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HomeAndConstructionBusiness",
            name: "JTB Floors",
            telephone: "+1-773-934-2004",
            url: "https://www.jtbfloorsinc.com/",
            logo: "https://www.jtbfloorsinc.com/og-image.jpg",
            image: "https://www.jtbfloorsinc.com/og-image.jpg",
            address: {
              "@type": "PostalAddress",
              streetAddress: "2313 Evergreen Ave",
              addressLocality: "Fox River Grove",
              addressRegion: "IL",
              postalCode: "60021",
              addressCountry: "US",
            },
            openingHours: "Mo-Fr 08:00-18:00",
            areaServed: { "@type": "GeoCircle", geoMidpoint: { "@type": "GeoCoordinates", latitude: 42.1539, longitude: -88.0595 }, geoRadius: "56327" },
            sameAs: [
              "https://www.facebook.com/profile.php?id=100064015626425#",
              "https://maps.app.goo.gl/Tbt848wzbXLTv6nb7",
            ],
          }),
        }}
      />

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            srcSet={`${hero640} 640w, ${hero1024} 1024w, ${hero1600} 1600w`}
            sizes="100vw"
            alt=""
            width={1804}
            height={1080}
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {showVideo && (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              src={heroVideo}
            />
          )}
          <div className="absolute inset-0 bg-[hsl(var(--hero-overlay)/0.8)] md:bg-transparent md:bg-gradient-to-r md:from-[hsl(var(--hero-overlay)/0.85)] md:via-[hsl(var(--hero-overlay)/0.6)] md:to-transparent" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[hsl(var(--card)/0.7)] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[hsl(var(--hero-overlay))] to-transparent" />
        </div>
        {/* Integrated nav pinned to top */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute top-4 left-0 right-0 z-10 px-4 lg:px-8"
        >
          {/* Logo - top left */}
          <Link to="/" className="absolute top-0 left-4 lg:left-8 flex-shrink-0">
            <img src={logo} srcSet={`${logo200} 200w, ${logo400} 400w, ${logo600} 600w`} sizes="(min-width: 768px) 288px, 168px" alt="JTB Floors" width={1536} height={1024} decoding="async" className="h-28 md:h-48 w-auto" />
          </Link>

          {/* Nav links - centered on page */}
          <nav className="hidden md:flex items-center justify-center gap-1 pt-2" aria-label="Hero navigation">
            {heroNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-4 py-2 text-sm font-medium text-white/90 hover:text-white rounded-md transition-colors drop-shadow-md"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Phone button - top right */}
          <div className="hidden md:flex items-center gap-3 absolute top-2 right-4 lg:right-8">
            <Button size="sm" asChild className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg font-bold text-base px-5 py-2.5">
              <a href="tel:17739342004">
                <Phone className="w-5 h-5" />
                (773) 934-2004
              </a>
            </Button>
          </div>
        </motion.div>

        <div className="relative container mx-auto px-4 lg:px-8 pt-32 md:pt-40 lg:pt-44 pb-8">
          <div className="max-w-2xl">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-card mb-6 leading-tight"
            >
              JTB Floors — Premium Flooring in Barrington, IL
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg md:text-xl text-card/80 mb-8 leading-relaxed"
            >
              35 years of expert hardwood, ceramic, laminate, tile & marble flooring. Residential & commercial. Materials and labor included.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button asChild size="lg" className="btn-shine text-lg px-8 py-6 gap-2">
                <a href="tel:17739342004">
                  <Phone className="w-5 h-5" /> Call (773) 934-2004
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild className="text-lg px-8 py-6 border-foreground/30 text-foreground bg-card hover:bg-muted">
                <Link to="/contact" hash="form">Request Estimate Online</Link>
              </Button>
            </motion.div>
            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-wrap gap-4 mt-10"
            >
              {trustBadges.map((badge, i) => (
                <div key={i} className="flex items-center gap-2 bg-card/10 backdrop-blur-sm border border-card/20 rounded-lg px-4 py-2">
                  <badge.icon className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-card">{badge.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--hero-overlay))" bottomColor="hsl(var(--background))" />

      {/* SERVICES GRID */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">Our Services</h2>
            <p className="text-center text-muted-foreground max-w-xl mx-auto mb-12">Expert flooring installation — hardwood, ceramic, laminate, tile & marble.</p>
          </SectionReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <SectionReveal key={i} delay={i * 0.1}>
                <div className="group bg-card border border-border rounded-xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:animate-bounce-micro">
                    <service.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold mb-2">{service.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{service.desc}</p>
                  <Link to="/contact" hash="form" className="inline-flex items-center text-sm font-medium text-primary hover:underline gap-1">
                    Request Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--muted))" />

      {/* HOW WE WORK */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">How We Work</h2>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
              From the first phone call to the final walkthrough, we make flooring simple. Here's what working with JTB Floors looks like.
            </p>
          </SectionReveal>
          <div className="max-w-3xl mx-auto">
            {steps.map((step, i) => (
              <SectionReveal key={i} delay={i * 0.08}>
                <div className="flex gap-5 mb-10 last:mb-0">
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="pt-1">
                    <h3 className="text-lg font-heading font-semibold mb-1">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* WHY CHOOSE US */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">Why Choose JTB Floors</h2>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
              We've been doing this for 35 years — and we do it differently. Here's why homeowners and businesses trust us with their floors.
            </p>
          </SectionReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whyUs.map((item, i) => (
              <SectionReveal key={i} delay={i * 0.08}>
                <div className="bg-card border border-border rounded-xl p-6 h-full hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <h3 className="font-heading font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
          <SectionReveal delay={0.5}>
            <div className="text-center mt-12">
              <Button asChild className="btn-shine gap-2">
                <a href="tel:17739342004">
                  <Phone className="w-5 h-5" /> Call (773) 934-2004
                </a>
              </Button>
            </div>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--muted))" />

      {/* GOOGLE REVIEWS */}
      <GoogleReviews />

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* MAP / SERVICE AREA */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-4">Our Location</h2>
            <p className="text-center text-muted-foreground max-w-xl mx-auto mb-10">
              Based in Fox River Grove, IL — serving Barrington and all areas within a 35-mile radius.
            </p>
          </SectionReveal>
          <SectionReveal delay={0.1}>
            <div className="rounded-xl overflow-hidden border border-border shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2958.5!2d-88.2156!3d42.2003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880f1c2b1b1b1b1b%3A0x1b1b1b1b1b1b1b1b!2s2313+Evergreen+Ave%2C+Fox+River+Grove%2C+IL+60021!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="JTB Floors location — 2313 Evergreen Ave, Fox River Grove, IL 60021"
                className="w-full"
              />
            </div>
            <div className="text-center mt-4">
              <a
                href="https://maps.app.goo.gl/Tbt848wzbXLTv6nb7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
              >
                Open in Google Maps
              </a>
            </div>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--secondary))" />

      {/* FINAL CTA */}
      <CTAStrip />
    </Layout>
  );
};

export default Index;
